import { useMemo } from 'react';

import { Link } from '@tanstack/react-router';
import { Truncate } from 'cpool-components/general/Truncate';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { PROCESSED_NOTIFICATION_KINDS } from 'cpool-constants';
import type { SchemaLoanRequestResolver_GetResubmissionLoanRequestsByDate } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { LuClock, LuEye } from 'react-icons/lu';

import { getNotificationLinkOptions } from '~/features/notifications/utils/getNotificationLinkOptions';
import { dateFormat, timeFormat } from '~/lib/i18n';
import { cn } from '~/utils/cnTailwind';

type Props = {
    data: SchemaLoanRequestResolver_GetResubmissionLoanRequestsByDate[number];
};

export const ResubmissionItem: React.FC<Props> = ({ data }) => {
    const { t } = useTranslation();

    const isInPast = useMemo(() => {
        if (data.resubmission === null) return false;
        return new Date().getTime() > data.resubmission;
    }, [data.resubmission]);

    const textColor = useMemo(() => {
        if (isInPast) return 'text-red-500';
        return 'text-midnight';
    }, [isInPast]);

    const resubmissionDate = data.resubmission
        ? `${dateFormat(data.resubmission)} - ${timeFormat(data.resubmission)}`
        : '';
    const createdAt = `${dateFormat(new Date(data.createdAt))} - ${timeFormat(new Date(data.createdAt))}`;

    return (
        <Link {...getNotificationLinkOptions(PROCESSED_NOTIFICATION_KINDS.RESUBMISSION, data.id)} target="_blank">
            <div className="hover:bg-surface-light py-2 px-4 flex items-center justify-between gap-2 rounded-md">
                <div className="flex flex-wrap items-center gap-x-6 min-w-[200px]">
                    <div>
                        <Truncate maxWidth="200px" className="mb-1 text-sm text-midnight min-w-[200px] max-w-[200px]">
                            {data.antragsteller1.personendaten?.vorname} {data.antragsteller1.personendaten?.nachname}
                        </Truncate>
                        <p className="text-xs text-midnight-placeholder">
                            <span>{createdAt}</span> {t('DASHBOARD.RESUBMISSIONS.DATE')}
                        </p>
                    </div>
                    <span className={cn('flex items-center gap-1 py-1.5 text-sm whitespace-nowrap', textColor)}>
                        <LuClock /> <span>{resubmissionDate}</span> {t('DASHBOARD.RESUBMISSIONS.DATE')}
                    </span>
                </div>
                <Button variant={ButtonVariant.SECONDARY} iconLeft={<LuEye />}>
                    {t('DASHBOARD.RESUBMISSIONS.CHECKOUT')}
                </Button>
            </div>
        </Link>
    );
};
